<template>
  <main v-if="sessionError" class="service-recovery" :class="`service-tone--${tone}`" aria-labelledby="service-recovery-title">
    <div class="service-recovery__layout">
      <BrandLogo class="service-recovery__brand" variant="zh-primary" tone="brand" />
      <section class="service-recovery__panel">
        <div class="service-recovery__visual" aria-hidden="true">
          <span class="service-recovery__symbol"><i :class="icon" /></span>
        </div>
        <div role="alert" class="service-recovery__message">
          <span class="service-status__label">{{ label }}</span>
          <h1 id="service-recovery-title">{{ headline }}</h1>
          <p>{{ summary }}</p>
        </div>
        <div v-if="!store.unavailable && store.status?.estimatedRecoveryAt" class="service-recovery__estimate">
          <i class="ri-time-line" aria-hidden="true" />
          <span>预计恢复</span><strong>{{ time(store.status.estimatedRecoveryAt) }}</strong>
        </div>
        <div class="service-recovery__actions">
          <a-button type="primary" @click="emit('retry-session')"><i class="ri-refresh-line" aria-hidden="true" /><span>重新连接</span></a-button>
          <a-button @click="detailsOpen = true"><i class="ri-information-line" aria-hidden="true" /><span>查看详情</span></a-button>
        </div>
      </section>
    </div>
  </main>
  <section v-else-if="store.hasBanner" class="service-status" :class="`service-tone--${tone}`" :role="tone === 'incident' || store.unavailable ? 'alert' : 'status'" aria-label="服务状态通知">
    <span class="service-status__icon" aria-hidden="true"><i :class="icon" /></span>
    <div class="service-status__content">
      <div class="service-status__heading"><span class="service-status__label">{{ label }}</span><strong>{{ headline }}</strong></div>
      <p>{{ summary }}</p>
      <small v-if="store.unavailable && store.status">公告暂时无法更新，可在详情中查看上次公告。</small>
    </div>
    <div class="service-status__actions">
      <a-button size="small" @click="detailsOpen = true">查看详情<i class="ri-arrow-right-s-line" aria-hidden="true" /></a-button>
      <a-button v-if="store.unavailable" size="small" :loading="store.refreshing" @click="store.refresh()">重试连接</a-button>
    </div>
  </section>
  <a-modal v-model:open="detailsOpen" title="服务状态详情" :footer="null" :width="480">
    <div class="service-status-dialog">
      <h2>{{ headline }}</h2>
      <p>{{ summary }}</p>
      <section v-if="store.unavailable && store.status" class="service-status-dialog__notice">
        <strong>上次公告 · {{ store.status.title }}</strong>
        <p>{{ store.status.message }}</p>
        <small>当前无法获取最新状态，以下时间来自上次公告。</small>
      </section>
      <dl v-if="store.status" class="service-status__details">
        <template v-if="store.status.startsAt"><dt>{{ store.status.phase === 'scheduled' ? '计划开始' : '开始时间' }}</dt><dd>{{ time(store.status.startsAt) }}</dd></template>
        <template v-if="store.status.estimatedRecoveryAt"><dt>预计恢复</dt><dd>{{ time(store.status.estimatedRecoveryAt) }}</dd></template>
        <template v-if="store.status.endsAt"><dt>公告结束</dt><dd>{{ time(store.status.endsAt) }}</dd></template>
      </dl>
      <section v-if="sessionError" class="service-status-dialog__notice"><strong>连接错误信息</strong><p>{{ sessionError }}</p></section>
      <p v-if="statusChecked" role="status">{{ store.unavailable ? '仍无法获取最新状态，请检查网络连接或稍后重试。' : store.status ? '已获取最新服务状态。' : '服务状态正常，可以尝试重新连接。' }}</p>
      <a-button :loading="store.refreshing" @click="checkStatus"><i class="ri-refresh-line" aria-hidden="true" /><span>检查服务状态</span></a-button>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import BrandLogo from '@/components/BrandLogo.vue';
import { useServiceStatusStore } from '@/stores/service-status.store';

const props = withDefaults(defineProps<{ sessionError?: string }>(), { sessionError: '' });
const emit = defineEmits<{ 'retry-session': [] }>();
const store = useServiceStatusStore();
const detailsOpen = ref(false);
const statusChecked = ref(false);
const tone = computed(() => store.unavailable || !store.status ? 'connection' : store.status.kind === 'incident' ? 'incident' : 'upgrade');
const label = computed(() => tone.value === 'connection' ? '连接异常' : `${store.status?.phase === 'scheduled' ? '计划' : ''}${tone.value === 'upgrade' ? '升级维护' : '服务故障'}`);
const icon = computed(() => tone.value === 'connection' ? 'ri-cloud-off-line' : tone.value === 'upgrade' ? 'ri-tools-line' : 'ri-server-line');
const headline = computed(() => {
  if (!store.unavailable && store.status) return store.status.title;
  return props.sessionError ? '暂时无法进入工作台' : '服务连接暂时中断';
});
const summary = computed(() => {
  if (!store.unavailable && store.status) return store.status.message;
  return props.sessionError
    ? '请检查网络连接，或稍后重试。'
    : '暂时无法获取服务状态，部分功能可能受到影响。请检查网络连接，或稍后重试。';
});
function time(value: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '暂未公布';
}
async function checkStatus(event: MouseEvent): Promise<void> {
  const trigger = event.currentTarget as HTMLButtonElement | null;
  const hadFocus = document.activeElement === trigger;
  statusChecked.value = false;
  await store.refresh();
  statusChecked.value = true;
  await nextTick();
  // 加载会禁用共享按钮，结束后恢复丢失的焦点，保留弹窗键盘操作。
  if (hadFocus && detailsOpen.value && document.activeElement === document.body) trigger?.focus();
}
</script>

<style scoped src="./service-status.css"></style>
